import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  Paper,
  Grid,
  Badge,
  ActionIcon,
  Alert,
  Select,
  Menu,
  TextInput,
  Box,
  rem,
  Table,
  NumberInput,
  Checkbox,
  Transition,
  Collapse,
  Tabs,
  Loader,
  Skeleton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconEggCracked,
  IconTrash,
  IconSearch,
  IconAlertCircle,
  IconBottle,
  IconChevronLeft,
  IconDotsVertical,
  IconCarrot,
  IconMeat,
  IconMilk,
  IconWheat,
  IconSalt,
  IconFridge,
  IconBaguette,
  IconPaperBag,
  IconSquareCheck,
  IconSquareMinusFilled,
  IconShoppingCart,
  IconList,
  IconPhoto,
  IconEye,
  IconMicrophone,
  IconVideo,
} from '@tabler/icons-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAppUser } from '../../hooks/useAppUser';
import { PantryItem } from '../../types/pantry';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { PantryItemDrawer } from './PantryItemDrawer';
import { ProductPreferences } from './ProductPreferences';
import { formatQuantity } from '../../utils/formatQuantity';
import { SplitButton } from '../UI/SplitButton';
import { ImportPantryModal } from './ImportPantryModal';
import { ImportPantryVoiceModal } from './ImportPantryVoiceModal';
import { ImportPantryVideoModal } from './ImportPantryVideoModal';
import { PantryReviewModal } from './PantryReviewModal';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger } from '../../hooks/useFirestoreTrigger';
import { getPageCache, setPageCache } from '../../utils/pageCache';

interface MyPantryProps {
  onBack: () => void;
  initialSearch?: string;
}

type PantryCacheEntry = {
  items: PantryItem[];
  importCounts: { pending: number; processing: number; failed: number };
};

export const MyPantry = ({ onBack, initialSearch }: MyPantryProps) => {
  const { user } = useAuth();
  // Effective user for Firestore listener targets: under admin impersonation
  // appUser.uid is the impersonated user's uid — whose pantry_imports the
  // REST API reads and the backend writes.
  const { user: appUser, profileLoaded: appProfileLoaded } = useAppUser();
  const effectiveUid = appUser?.uid || user?.uid;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isVerySmall = useMediaQuery('(max-width: 400px)');
  const [activeTab, setActiveTab] = useState<string>('items');
  // Seed from the pantry this session last displayed, so a back navigation
  // renders items on the first paint and the mount fetch refreshes silently.
  const pantryCacheKey = effectiveUid ? `pantry:${effectiveUid}` : null;
  const seededPantry = getPageCache<PantryCacheEntry>(pantryCacheKey);
  const [pantryItems, setPantryItems] = useState<PantryItem[]>(seededPantry?.items ?? []);
  const [loading, setLoading] = useState(!seededPantry);
  const [error, setError] = useState<string | null>(null);
  // Kroger state derives from the merged app user's profile — session-cached
  // on remounts, refreshed by useAppUser — instead of a bespoke profile fetch.
  const isKrogerConnected = appUser?.familyProfile?.krogerConnected || false;
  const profileLoaded = !!appUser?.familyProfile || appProfileLoaded;
  const [importCounts, setImportCounts] = useState<{
    pending: number;
    processing: number;
    failed: number;
  }>(seededPantry?.importCounts ?? { pending: 0, processing: 0, failed: 0 });
  const hasPantryDataRef = useRef(!!seededPantry);
  const [searchQuery, setSearchQuery] = useState(initialSearch || '');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [modalOpened, setModalOpened] = useState(false);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [voiceImportModalOpened, setVoiceImportModalOpened] = useState(false);
  const [videoImportModalOpened, setVideoImportModalOpened] = useState(false);
  const [reviewModalOpened, setReviewModalOpened] = useState(false);
  const [editingItem, setEditingItem] = useState<PantryItem | undefined>(undefined);
  const [editingQuantity, setEditingQuantity] = useState<string | null>(null);
  const [tempQuantity, setTempQuantity] = useState<string | number>('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [optimisticallyRemovedItems, setOptimisticallyRemovedItems] = useState<Set<string>>(new Set());
  
  // Ref to trigger add preference modal
  const addPreferenceRef = useRef<(() => void) | null>(null);

  const categories: { value: string; label: string }[] = [
    { value: 'all', label: 'All categories' },
    { value: 'produce', label: 'Produce' },
    { value: 'meat', label: 'Meat' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'pantry_dry_goods', label: 'Pantry & dry goods' },
    { value: 'spices_seasonings', label: 'Spices & seasonings' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'bakery', label: 'Bakery' },
    { value: 'beverages', label: 'Beverages' },
    { value: 'other', label: 'Other' },
  ];

  const types: { value: string; label: string }[] = [
    { value: 'all', label: 'All types' },
    { value: 'staple', label: 'Staples only' },
    { value: 'ingredient', label: 'Ingredients only' },
  ];

  

  const fetchPantryItems = useCallback(async (showLoading: boolean = true) => {
    if (!user) return;
    
    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pantry items');
      }

      const data = await response.json();
      hasPantryDataRef.current = true;
      setPantryItems(data.items || []);
      const newImportCounts = data.importCounts || { pending: 0, processing: 0, failed: 0 };
      setImportCounts(newImportCounts);
    } catch (error: any) {
      console.error('Error fetching pantry items:', error);
      if (showLoading) {
        setError(error.message || 'Failed to load pantry items');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [user]);

  // Initial fetch — with seeded content this is a silent background refresh.
  useEffect(() => {
    fetchPantryItems(!hasPantryDataRef.current);
  }, [fetchPantryItems]);

  // Mirror displayed data into the session cache so local mutations (edits,
  // optimistic removes) are what a back navigation resumes from.
  useEffect(() => {
    if (hasPantryDataRef.current) {
      setPageCache(pantryCacheKey, { items: pantryItems, importCounts });
    }
  }, [pantryItems, importCounts, pantryCacheKey]);

  // The import banner's Collapse animates closed after the counts reach 0;
  // keep the last non-zero total so the exiting banner doesn't flash
  // "0 pantry imports are processing". Same for the review alert's count.
  const lastImportTotalRef = useRef(1);
  const lastReviewCountRef = useRef(1);
  useEffect(() => {
    const total = importCounts.pending + importCounts.processing;
    if (total > 0) {
      lastImportTotalRef.current = total;
    }
  }, [importCounts]);

  // Realtime refresh while there are active imports: watch the user's
  // in-flight pantry import batches and refetch the pantry whenever one
  // changes or reaches a terminal status (i.e. leaves the query).
  const hasActiveImports = importCounts.pending > 0 || importCounts.processing > 0;
  const inFlightPantryImportsQuery = useMemo(
    () =>
      effectiveUid
        ? query(
            collection(db, 'users', effectiveUid, 'pantry_imports'),
            where('importStatus.status', 'in', ['pending', 'processing'])
          )
        : null,
    [effectiveUid]
  );
  useFirestoreTrigger({
    enabled: hasActiveImports,
    target: inFlightPantryImportsQuery,
    onTrigger: () => fetchPantryItems(false), // false = no loading spinner
  });

  // Suppress ResizeObserver errors - this is a common, harmless browser issue
  // Reference: https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => window.removeEventListener('error', handleError);
  }, []);

  const handleDeleteItem = async (itemId: string) => {
    if (!user) return;

    // Find the item for notification purposes
    const item = pantryItems.find(i => i.id === itemId);
    if (!item) return;

    // Optimistically remove item from UI immediately
    setOptimisticallyRemovedItems(prev => new Set(prev).add(itemId));
    
    // Also remove from selected items
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });

    const notificationId = `delete-pantry-${itemId}`;
    
    // Create undo button component with loading state
    const UndoButton = () => {
      const [isUndoing, setIsUndoing] = useState(false);
      
      return (
        <Button
          size="xs"
          variant="subtle"
          color="orange"
          loading={isUndoing}
          onClick={async () => {
            setIsUndoing(true);
            try {
              await handleUndoDeleteItem(item, notificationId);
            } finally {
              setIsUndoing(false);
            }
          }}
        >
          Undo
        </Button>
      );
    };
    
    // Show undo notification immediately
    notifications.show({
      id: notificationId,
      title: `Deleted "${item.displayName}"`,
      message: (
        <Group>
          <Text size="sm">Item has been removed from your pantry</Text>
          <UndoButton />
        </Group>
      ),
      color: 'orange',
      autoClose: 5000,
      withCloseButton: true,
    });

    // Perform actual API call in background
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete pantry item');
      }

      // Remove from actual pantry items state
      setPantryItems(prev => prev.filter(i => i.id !== itemId));
    } catch (error: any) {
      console.error('Error deleting pantry item:', error);
      
      // If API call failed, restore the item
      setOptimisticallyRemovedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      
      notifications.show({
        title: 'Error',
        message: 'Failed to delete item. Please try again.',
        color: 'red',
      });
    }
  };

  const handleUndoDeleteItem = async (item: PantryItem, notificationId: string) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry/${item.id}/undo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore pantry item');
      }

      // Add item back to the pantry items array
      setPantryItems(prev => {
        // Check if item already exists to avoid duplicates
        const exists = prev.some(i => i.id === item.id);
        return exists ? prev : [...prev, item];
      });
      
      // Remove from optimistically removed set so item reappears
      setOptimisticallyRemovedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });

      notifications.hide(notificationId);
    } catch (error: any) {
      console.error('Error restoring pantry item:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to restore item',
        color: 'red',
      });
    }
  };

  const handleBulkDelete = async () => {
    if (!user || selectedItems.size === 0) return;

    setBulkDeleteLoading(true);
    
    const selectedItemIds = Array.from(selectedItems);
    const itemsToDelete = pantryItems.filter(item => selectedItemIds.includes(item.id));
    
    // Optimistically remove all items from UI immediately
    selectedItemIds.forEach(itemId => {
      setOptimisticallyRemovedItems(prev => new Set(prev).add(itemId));
    });
    
    // Clear selection
    setSelectedItems(new Set());
    
    const notificationId = `bulk-delete-pantry-${Date.now()}`;
    
    // Create undo button component with loading state
    const UndoAllButton = () => {
      const [isUndoing, setIsUndoing] = useState(false);
      
      return (
        <Button
          size="xs"
          variant="subtle"
          color="orange"
          loading={isUndoing}
          onClick={async () => {
            setIsUndoing(true);
            try {
              await handleUndoBulkDelete(itemsToDelete, notificationId);
            } finally {
              setIsUndoing(false);
            }
          }}
        >
          Undo All
        </Button>
      );
    };
    
    // Show single undo notification for all items
    notifications.show({
      id: notificationId,
      title: `Deleted ${itemsToDelete.length} items`,
      message: (
        <Group>
          <Text size="sm">Items have been removed from your pantry</Text>
          <UndoAllButton />
        </Group>
      ),
      color: 'orange',
      autoClose: 5000,
      withCloseButton: true,
    });

    // Perform actual API calls in background for all items
    const token = await user.getIdToken();
    let failedItems: string[] = [];
    
    for (const itemId of selectedItemIds) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry/${itemId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          // Remove from actual pantry items state on success
          setPantryItems(prev => prev.filter(i => i.id !== itemId));
        } else {
          failedItems.push(itemId);
        }
      } catch (error) {
        failedItems.push(itemId);
      }
    }
    
    // If some deletions failed, restore those items
    if (failedItems.length > 0) {
      setOptimisticallyRemovedItems(prev => {
        const newSet = new Set(prev);
        failedItems.forEach(itemId => newSet.delete(itemId));
        return newSet;
      });
      
      notifications.hide(notificationId);
      notifications.show({
        title: 'Some deletions failed',
        message: `${selectedItemIds.length - failedItems.length} items deleted, ${failedItems.length} failed. Please try again.`,
        color: 'yellow',
      });
    }
    
    setBulkDeleteLoading(false);
  };

  const handleUndoBulkDelete = async (items: PantryItem[], notificationId: string) => {
    if (!user) return;

    const token = await user.getIdToken();
    let restoredItems: PantryItem[] = [];
    let failedItems: string[] = [];

    // Try to restore all items
    for (const item of items) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry/${item.id}/undo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          restoredItems.push(item);
        } else {
          failedItems.push(item.id);
        }
      } catch (error) {
        failedItems.push(item.id);
      }
    }

    // Add successfully restored items back to pantry items array
    if (restoredItems.length > 0) {
      setPantryItems(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const newItems = restoredItems.filter(item => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
      
      // Remove restored items from optimistically removed set
      setOptimisticallyRemovedItems(prev => {
        const newSet = new Set(prev);
        restoredItems.forEach(item => newSet.delete(item.id));
        return newSet;
      });
    }

    notifications.hide(notificationId);

    // Only show notification if there were errors
    if (failedItems.length > 0) {
      if (restoredItems.length > 0) {
        // Some items restored
        notifications.show({
          title: 'Partial restore',
          message: `${restoredItems.length} items restored, ${failedItems.length} failed`,
          color: 'yellow',
        });
      } else {
        // No items restored
        notifications.show({
          title: 'Restore failed',
          message: 'Failed to restore items',
          color: 'red',
        });
      }
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (category?: string) => {
    const itemsToSelect = category ? groupedItems[category] : filteredItems;
    const allSelected = itemsToSelect.every(item => selectedItems.has(item.id));
    
    if (allSelected) {
      // Deselect all items in this category/view
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        itemsToSelect.forEach(item => newSet.delete(item.id));
        return newSet;
      });
    } else {
      // Select all items in this category/view
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        itemsToSelect.forEach(item => newSet.add(item.id));
        return newSet;
      });
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleQuantityEdit = (item: PantryItem) => {
    if (item.type === 'staple') return; // Staples don't have editable quantities
    
    setEditingQuantity(item.id);
    setTempQuantity(item.quantity || 0);
  };

  const handleQuantityUpdate = async (item: PantryItem) => {
    const numValue = typeof tempQuantity === 'string' ? parseFloat(tempQuantity) : tempQuantity;
    
    if (!user || tempQuantity === '' || isNaN(numValue) || numValue <= 0) {
      setEditingQuantity(null);
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...item,
          quantity: numValue,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update pantry item');
      }

      const updatedItem = await response.json();
      
      // Update local state
      setPantryItems(prev => prev.map(i => 
        i.id === item.id ? updatedItem.item : i
      ));
      
      setEditingQuantity(null);
    } catch (error: any) {
      console.error('Error updating pantry item:', error);
      setError(error.message || 'Failed to update item');
      setEditingQuantity(null);
    }
  };

  const handleQuantityCancel = () => {
    setEditingQuantity(null);
    setTempQuantity('');
  };

  const handleAddItem = () => {
    setEditingItem(undefined);
    setModalOpened(true);
  };

  const handleImportFromImages = () => {
    setImportModalOpened(true);
  };

  const handleImportFromVoice = () => {
    setVoiceImportModalOpened(true);
  };

  const handleImportFromVideo = () => {
    setVideoImportModalOpened(true);
  };

  const handleImportStarted = () => {
    // Trigger a refresh to start polling for import status
    fetchPantryItems(false);
  };

  const handleOpenReview = () => {
    setReviewModalOpened(true);
  };

  const handleReviewCompleted = () => {
    // Refetch pantry items after review is completed
    const fetchPantryItems = async () => {
      if (!user) return;

      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPantryItems(data.items || []);
        }
      } catch (error) {
        console.error('Error refetching pantry items:', error);
      }
    };

    fetchPantryItems();
  };

  const handleEditItem = (item: PantryItem) => {
    setEditingItem(item);
    setModalOpened(true);
  };

  const handleSaveItem = (savedItem: PantryItem) => {
    if (editingItem) {
      // Update existing item
      setPantryItems(prev => prev.map(item => 
        item.id === savedItem.id ? savedItem : item
      ));
    } else {
      // Add new item
      setPantryItems(prev => [...prev, savedItem]);
    }
  };

  const handleDuplicateFound = (existingItem: PantryItem) => {
    // Show confirmation modal explaining the situation
    modals.openConfirmModal({
      title: 'Item already exists',
      children: (
        <Text size="sm">
          <strong>{existingItem.displayName}</strong> is already in your pantry.
          <br /><br />
          Would you like to edit the existing item instead?
        </Text>
      ),
      labels: { confirm: 'Edit existing item', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: () => {
        // Close add modal and open edit modal
        setModalOpened(false);
        setTimeout(() => {
          setEditingItem(existingItem);
          setModalOpened(true);
        }, 100);
      },
      onCancel: () => {
        // Just close the add modal, user can try again
        setModalOpened(false);
      },
    });
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setEditingItem(undefined);
  };

  // Items net of optimistic removes — the pantry as the user currently sees
  // it. Gates the search/filter toolbar and empty-state variant: searching an
  // empty pantry is meaningless, and it misleads new users into treating the
  // search box as a way to find items to add.
  const visiblePantryItems = pantryItems.filter(
    item => !optimisticallyRemovedItems.has(item.id)
  );
  const pantryHasItems = visiblePantryItems.length > 0;

  const filteredItems = visiblePantryItems.filter(item => {
    const matchesSearch = item.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  const groupedItems = filteredItems.reduce((groups, item) => {
    const category = item.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, PantryItem[]>);

  const getCategoryLabel = (category: string) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  // Count items needing review
  const itemsNeedingReview = pantryItems.filter(item => item.needsReview);
  const reviewCount = itemsNeedingReview.length;

  // The review alert's Collapse animates closed after the count reaches 0;
  // keep the last non-zero count so the exiting alert doesn't flash
  // "0 imported items need your review".
  useEffect(() => {
    if (reviewCount > 0) {
      lastReviewCountRef.current = reviewCount;
    }
  }, [reviewCount]);
  const displayReviewCount = reviewCount || lastReviewCountRef.current;

  const getCategoryIcon = (category: string) => {
    const iconProps = { size: 18 };
    switch (category) {
      case 'produce':
        return <IconCarrot {...iconProps} />;
      case 'meat':
        return <IconMeat {...iconProps} />;
      case 'dairy':
        return <IconMilk {...iconProps} />;
      case 'pantry_dry_goods':
        return <IconWheat {...iconProps} />;
      case 'spices_seasonings':
        return <IconSalt {...iconProps} />;
      case 'frozen':
        return <IconFridge {...iconProps} />;
      case 'bakery':
        return <IconBaguette {...iconProps} />;
      case 'beverages':
        return <IconBottle {...iconProps} />;
      default:
        return <IconPaperBag {...iconProps} />;
    }
  };


  // Pantry items content (reusable)
  const pantryItemsContent = loading ? (
    <Stack gap={28}>
      {/* Skeleton search/filter bar */}
      <Paper p="md" radius="lg" withBorder>
        <Stack gap="md">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Skeleton height={36} radius="md" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Skeleton height={36} radius="md" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <Skeleton height={36} radius="md" />
            </Grid.Col>
          </Grid>
          <Skeleton height={20} width={120} />
        </Stack>
      </Paper>
      {/* Skeleton category tables */}
      <Stack gap="xl">
      {Array.from({ length: 3 }).map((_, i) => (
        <Stack key={i} gap="md">
          <Group gap="xs">
            <Skeleton height={18} width={18} radius="sm" />
            <Skeleton height={22} width={120} />
          </Group>
          <Paper radius="lg" withBorder>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '40px' }}><Skeleton height={20} width={20} radius="sm" /></Table.Th>
                  <Table.Th><Skeleton height={14} width={30} /></Table.Th>
                  {!isMobile && <Table.Th style={{ width: '25%' }}><Skeleton height={14} width={50} /></Table.Th>}
                  {!isMobile && <Table.Th style={{ width: '25%' }}><Skeleton height={14} width={30} /></Table.Th>}
                  <Table.Th style={{ width: '60px' }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Array.from({ length: 4 }).map((_, j) => (
                  <Table.Tr key={j}>
                    <Table.Td><Skeleton height={18} width={18} radius="sm" /></Table.Td>
                    <Table.Td><Skeleton height={14} width="70%" /></Table.Td>
                    {!isMobile && <Table.Td><Skeleton height={14} width={60} /></Table.Td>}
                    {!isMobile && <Table.Td><Skeleton height={18} width={70} radius="xl" /></Table.Td>}
                    <Table.Td><Skeleton height={28} width={28} radius="md" /></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      ))}
      </Stack>
    </Stack>
  ) : (
    <Stack gap="lg">
        {/* Active Import Notification */}
        <Collapse in={importCounts.pending > 0 || importCounts.processing > 0}>
          <Alert icon={<Loader size={16} color="blue" />} color="blue" variant="light" radius="lg">
            <Text size="sm" c="blue.7">
              {(() => {
                const total =
                  importCounts.pending + importCounts.processing ||
                  lastImportTotalRef.current;
                return total === 1
                  ? 'Pantry import is processing...'
                  : `${total} pantry imports are processing...`;
              })()}
            </Text>
          </Alert>
        </Collapse>

        {/* Review Alert */}
        <Collapse in={reviewCount > 0}>
          <Alert
            icon={<IconEye size={16} />}
            title="Items need review"
            color="orange"
            variant="light"
          >
            <Group justify="space-between" align="flex-start">
              <Text size="sm">
                {displayReviewCount} imported item{displayReviewCount !== 1 ? 's' : ''} need{displayReviewCount === 1 ? 's' : ''} your review before being added to your pantry.
              </Text>
              <Button
                variant="light"
                color="orange"
                size="xs"
                onClick={handleOpenReview}
              >
                Review {displayReviewCount} item{displayReviewCount !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Alert>
        </Collapse>

        {/* Error Message */}
        <Collapse in={!!error}>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        </Collapse>

        {/* Search and Filter — only when there is a pantry to search */}
        {pantryHasItems && (
        <Paper p="md" radius="lg" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  placeholder="Search pantry items..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Select
                  placeholder="Filter by category"
                  value={categoryFilter}
                  onChange={(value) => setCategoryFilter(value || 'all')}
                  data={categories}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Select
                  placeholder="Filter by type"
                  value={typeFilter}
                  onChange={(value) => setTypeFilter(value || 'all')}
                  data={types}
                />
              </Grid.Col>
            </Grid>

            {/* Item count display */}
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                {filteredItems.length === 0
                  ? 'No items found'
                  : `${filteredItems.length} item${filteredItems.length === 1 ? '' : 's'} found`
                }
              </Text>
              {(searchQuery || categoryFilter !== 'all' || typeFilter !== 'all') && filteredItems.length > 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Group>
          </Stack>
        </Paper>
        )}

        {/* Bulk Actions Toolbar */}
        <Transition
          mounted={selectedItems.size > 0}
          transition="slide-down"
          duration={200}
        >
          {(styles) => (
            <Paper
              p="sm"
              withBorder
              radius="lg"
              style={styles}
              bg="var(--mantine-color-gray-0)"
            >
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={clearSelection}
                    title="Clear selection"
                  >
                    <IconSquareMinusFilled size={20} />
                  </ActionIcon>
                  <Text size="sm" c="dimmed">
                    {selectedItems.size} selected
                  </Text>
                </Group>
                <Button
                  color="red"
                  variant="light"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleBulkDelete}
                  loading={bulkDeleteLoading}
                >
                  Delete
                </Button>
              </Group>
            </Paper>
          )}
        </Transition>

        {/* Empty State */}
        {filteredItems.length === 0 && !loading && (
          pantryHasItems ? (
            /* Items exist but the search/filters match none of them */
            <Paper p="xl" radius="lg" withBorder>
              <Stack gap="md" align="center">
                <IconEggCracked size={48} color="var(--mantine-color-gray-5)" />
                <Text fw={500} ta="center">
                  No items found matching your filters
                </Text>
                <Text c="dimmed" size="sm" ta="center">
                  Try adjusting your search or filter criteria
                </Text>
                {(searchQuery || categoryFilter !== 'all' || typeFilter !== 'all') && (
                  <Button
                    variant="light"
                    onClick={() => {
                      setSearchQuery('');
                      setCategoryFilter('all');
                      setTypeFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </Stack>
            </Paper>
          ) : (
            /* True zero state: explain the pantry's purpose and lead with the
               fastest ways to fill it */
            <Paper p="xl" radius="lg" withBorder>
              <Stack gap="lg" align="center" py="md">
                <IconEggCracked size={48} color="var(--mantine-color-gray-5)" />
                <Stack gap="xs" align="center">
                  <Title order={3} ta="center">
                    Tell us what's already in your kitchen
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={460}>
                    Meal plans cook with what you have, and shopping lists skip it.
                  </Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Button
                    leftSection={<IconPhoto size={16} />}
                    onClick={handleImportFromImages}
                  >
                    Import from photos
                  </Button>
                  <Text c="dimmed" size="xs" ta="center">
                    Snap your shelves — we'll identify the items
                  </Text>
                </Stack>
                <Group gap="xs" justify="center">
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconMicrophone size={14} />}
                    onClick={handleImportFromVoice}
                  >
                    Import from voice
                  </Button>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconVideo size={14} />}
                    onClick={handleImportFromVideo}
                  >
                    Import from video
                  </Button>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleAddItem}
                  >
                    Add items manually
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )
        )}

        {/* Pantry Items by Category */}
        <Stack gap="xl">
          {Object.keys(groupedItems).map(category => (
            <Stack key={category} gap="md">
              <Group gap="xs">
                {getCategoryIcon(category)}
                <Title order={4}>{getCategoryLabel(category)}</Title>
              </Group>
            <Paper radius="lg" withBorder>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '40px' }}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        mt={4}
                        onClick={() => handleSelectAll(category)}
                        title={`Select all ${getCategoryLabel(category).toLowerCase()}`}
                      >
                        {groupedItems[category].every(item => selectedItems.has(item.id)) && groupedItems[category].length > 0 ? (
                          <IconSquareMinusFilled size={20} />
                        ) : (
                          <IconSquareCheck size={20} />
                        )}
                      </ActionIcon>
                    </Table.Th>
                    <Table.Th>Item</Table.Th>
                    {!isMobile && <Table.Th style={{ width: '25%' }}>Quantity</Table.Th>}
                    {!isMobile && <Table.Th style={{ width: '25%' }}>Type</Table.Th>}
                    <Table.Th style={{ width: '60px' }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groupedItems[category].map(item => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <Checkbox
                          size="sm"
                          checked={selectedItems.has(item.id)}
                          onChange={(e) => handleSelectItem(item.id, e.currentTarget.checked)}
                          styles={{
                            input: { cursor: 'pointer' },
                            label: { cursor: 'pointer' }
                          }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          <Group gap="xs" wrap="nowrap">
                            <Text fw={500} size="sm" style={{ wordBreak: 'break-word' }}>{item.displayName}</Text>
                            {item.needsReview && (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color="orange"
                                  onClick={handleOpenReview}
                                  style={{ cursor: 'pointer', flexShrink: 0 }}
                                >
                                  Needs review
                                </Badge>
                            )}
                          </Group>
                          {isMobile && (
                            <Group gap="xs">
                              <Badge
                                size="xs"
                                variant="light"
                                color={item.type === 'staple' ? 'green' : 'blue'}
                              >
                                {item.type === 'staple' ? 'Staple' : 'Ingredient'}
                              </Badge>
                              {item.type === 'ingredient' && item.quantity && item.unit && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleQuantityEdit(item)}
                                >
                                  {formatQuantity(item.quantity, item.unit)}
                                </Text>
                              )}
                            </Group>
                          )}
                        </Stack>
                      </Table.Td>
                      {!isMobile && (
                        <Table.Td>
                          {item.type === 'ingredient' && item.quantity && item.unit ? (
                            editingQuantity === item.id ? (
                              <NumberInput
                                size="xs"
                                value={tempQuantity}
                                onChange={(value) => setTempQuantity(value)}
                                min={0}
                                step={0.25}
                                decimalScale={2}
                                w={80}
                                onBlur={() => handleQuantityUpdate(item)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleQuantityUpdate(item);
                                  } else if (e.key === 'Escape') {
                                    handleQuantityCancel();
                                  }
                                }}
                                autoFocus
                              />
                            ) : (
                              <Text
                                size="sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleQuantityEdit(item)}
                              >
                                {formatQuantity(item.quantity, item.unit)}
                              </Text>
                            )
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                      )}
                      {!isMobile && (
                        <Table.Td>
                          <Group gap="xs">
                            <Badge
                              size="xs"
                              variant="light"
                              color={item.type === 'staple' ? 'green' : 'blue'}
                            >
                              {item.type === 'staple' ? 'Staple' : 'Ingredient'}
                            </Badge>
                          </Group>
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Menu shadow="md" width={180}>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              size="lg"
                            >
                              <IconDotsVertical size={20} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => handleEditItem(item)}
                              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                            >
                              Edit item
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                              color="red"
                              onClick={() => handleDeleteItem(item.id)}
                              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                            >
                              Delete item
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Stack>
          ))}
        </Stack>

          {/* Bottom padding for mobile */}
          <Box h="2rem" />
    </Stack>
  );

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={onBack}
              size="lg"
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={0} className="page-header">
              <Title order={2}>My pantry</Title>
              <Text c="dimmed" size="sm" className="page-subtitle">
                {pantryItems.length} item{pantryItems.length !== 1 ? 's' : ''} in your pantry
              </Text>
            </Stack>
          </Group>
          {(activeTab === 'items' || !isKrogerConnected) && (
            <>
              {/* Desktop: SplitButton */}
              <Group visibleFrom="sm">
                <SplitButton
                  mainAction={{
                    label: "Import from images",
                    onClick: handleImportFromImages,
                    leftSection: <IconPhoto size={16} />
                  }}
                  menuItems={[
                    {
                      label: "Import from voice",
                      onClick: handleImportFromVoice,
                      leftSection: <IconMicrophone size={16} />
                    },
                    {
                      label: "Import from video",
                      onClick: handleImportFromVideo,
                      leftSection: <IconVideo size={16} />
                    },
                    {
                      label: "Add pantry item",
                      onClick: handleAddItem,
                      leftSection: <IconPlus size={16} />
                    }
                  ]}
                />
              </Group>

              {/* Mobile: Single menu with all options */}
              <Group hiddenFrom="sm">
                <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="orange"
                      size="lg"
                    >
                      <IconPlus size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Add items</Menu.Label>
                    <Menu.Item
                      leftSection={<IconPhoto size={18} stroke={1.5} />}
                      onClick={handleImportFromImages}
                      style={{ fontSize: '16px', padding: '12px 16px' }}
                    >
                      Import from images
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconMicrophone size={18} stroke={1.5} />}
                      onClick={handleImportFromVoice}
                      style={{ fontSize: '16px', padding: '12px 16px' }}
                    >
                      Import from voice
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconVideo size={18} stroke={1.5} />}
                      onClick={handleImportFromVideo}
                      style={{ fontSize: '16px', padding: '12px 16px' }}
                    >
                      Import from video
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPlus size={18} stroke={1.5} />}
                      onClick={handleAddItem}
                      style={{ fontSize: '16px', padding: '12px 16px' }}
                    >
                      Add pantry item
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </>
          )}
          {activeTab === 'preferences' && isKrogerConnected && (
            <>
              {isMobile ? (
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  size="lg"
                  onClick={() => addPreferenceRef.current && addPreferenceRef.current()}
                >
                  <IconPlus size={20} />
                </ActionIcon>
              ) : (
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => addPreferenceRef.current && addPreferenceRef.current()}
                >
                  Add preference
                </Button>
              )}
            </>
          )}
        </Group>

        {/* Tabs - show skeleton tabs before profile loads, real tabs if Kroger connected */}
        {!profileLoaded ? (
          <Box>
            <Skeleton height={34} radius="md" />
            <Box pt={14}>{pantryItemsContent}</Box>
          </Box>
        ) : isKrogerConnected ? (
          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'items')}>
            <Tabs.List>
              <Tabs.Tab value="items" leftSection={<IconList size={14} />}>
                {isVerySmall ? 'Pantry' : 'Pantry items'}
              </Tabs.Tab>
              <Tabs.Tab value="preferences" leftSection={<IconShoppingCart size={14} />}>
                {isVerySmall ? 'Shopping' : 'Shopping preferences'}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="items" pt="md">
              {pantryItemsContent}
            </Tabs.Panel>

          <Tabs.Panel value="preferences" pt="md">
            <ProductPreferences
              onAddPreferenceRef={(ref) => { addPreferenceRef.current = ref; }}
            />
          </Tabs.Panel>

        </Tabs>
        ) : (
          pantryItemsContent
        )}

        {/* Add/Edit Drawer */}
        <PantryItemDrawer
          opened={modalOpened}
          onClose={handleCloseModal}
          item={editingItem}
          onSave={handleSaveItem}
          onDuplicateFound={handleDuplicateFound}
        />

        {/* Import Pantry Modal */}
        <ImportPantryModal
          opened={importModalOpened}
          onClose={() => setImportModalOpened(false)}
          onImportStarted={handleImportStarted}
        />

        {/* Voice Import Modal */}
        <ImportPantryVoiceModal
          opened={voiceImportModalOpened}
          onClose={() => setVoiceImportModalOpened(false)}
          onImportStarted={handleImportStarted}
        />

        {/* Video Import Modal */}
        <ImportPantryVideoModal
          opened={videoImportModalOpened}
          onClose={() => setVideoImportModalOpened(false)}
          onImportStarted={handleImportStarted}
        />

        {/* Review Pantry Modal */}
        <PantryReviewModal
          opened={reviewModalOpened}
          onClose={() => setReviewModalOpened(false)}
          onReviewCompleted={handleReviewCompleted}
        />
      </Stack>
    </MobileOptimized>
  );
};
